import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCar,
  faWalking,
  faSpinner,
  faRoute,
  faMapMarkerAlt,
  faArrowRight,
  faTurnLeft,
  faTurnRight,
  faArrowUp,
} from '@fortawesome/pro-regular-svg-icons';

import {
  useAppDispatch,
  useAppSelector,
  formatRouteDistance,
  formatRouteDuration,
  routeStepsSelector,
  routeSummarySelector,
  routeModeSelector,
  routeErrorSelector,
  selectedWaypointIdsSelector,
  setShowRouteSidebar,
  setCurrentRouteMode,
  setRouteError,
  useRouteCalculation,
} from '../../../store';
import { WaypointSelector } from './WaypointSelector';

import './RouteSidebar.scss';

export function RouteSidebar() {
  const dispatch = useAppDispatch();

  const selectedWaypointIds = useAppSelector(selectedWaypointIdsSelector);
  const currentMode = useAppSelector(routeModeSelector);
  const error = useAppSelector(routeErrorSelector);
  const routeSteps = useAppSelector(routeStepsSelector);
  const routeSummary = useAppSelector(routeSummarySelector);

  const { isCalculatingRoute: isCalculating } = useRouteCalculation();

  useEffect(() => {
    if (selectedWaypointIds.length >= 2 && !isCalculating && error) {
      dispatch(setRouteError(null));
    }
  }, [selectedWaypointIds.length, isCalculating, error, dispatch]);

  const getManeuverIcon = (maneuverType: string, modifier?: string) => {
    switch (maneuverType) {
    case 'turn':
      return modifier === 'left' ? faTurnLeft : faTurnRight;
    case 'depart':
    case 'arrive':
      return faMapMarkerAlt;
    case 'continue':
    case 'straight':
      return faArrowUp;
    default:
      return faArrowRight;
    }
  };


  return (
    <aside className={'route-sidebar open'} aria-label="Route planning sidebar">
      <header className="sidebar-header">
        <h2>
          <FontAwesomeIcon icon={faRoute} aria-hidden="true" />
          Route Planning
        </h2>
        <button
          className="close-btn"
          onClick={() => dispatch(setShowRouteSidebar(false))}
          title="Close route planning sidebar"
        >
          <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
        </button>
      </header>

      <div className="sidebar-content">
        {/* Route Planning Section */}
        <section className="route-planning" aria-labelledby="route-planning-heading">
          <h3 id="route-planning-heading" aria-hidden="true">Route Planning</h3>
          <div className="location-inputs">
            <WaypointSelector />
          </div>

          {/* Route Mode Toggle */}
          {selectedWaypointIds.length >= 2 && (
            <div className="route-mode-toggle" role="group" aria-label="Route mode selection">
              <button
                className={`mode-btn ${currentMode === 'walking' ? 'active' : ''}`}
                onClick={() => dispatch(setCurrentRouteMode('walking'))}
                disabled={isCalculating}
                aria-pressed={currentMode === 'walking'}
                aria-label="Set route mode to walking"
              >
                <FontAwesomeIcon icon={faWalking} aria-hidden="true" />
                Walk
              </button>
              <button
                className={`mode-btn ${currentMode === 'driving' ? 'active' : ''}`}
                onClick={() => dispatch(setCurrentRouteMode('driving'))}
                disabled={isCalculating}
                aria-pressed={currentMode === 'driving'}
                aria-label="Set route mode to driving"
              >
                <FontAwesomeIcon icon={faCar} aria-hidden="true" />
                Drive
              </button>
            </div>
          )}
        </section>

        {/* Route Summary */}
        {routeSummary && !isCalculating && (
          <section className="route-summary" aria-labelledby="route-summary-heading">
            <h3 id="route-summary-heading" aria-hidden="true">Route Summary</h3>
            <dl className="summary-stats">
              <div className="stat">
                <dt className="label" style={{ display: 'inline' }}>Duration:</dt>&nbsp;
                <dd className="value" style={{ display: 'inline', marginLeft: '0' }}>{formatRouteDuration(routeSummary.duration)}</dd>
              </div>
              <div className="stat">
                <dt className="label" style={{ display: 'inline' }}>Distance:</dt>&nbsp;
                <dd className="value" style={{ display: 'inline', marginLeft: '0'  }}>{formatRouteDistance(routeSummary.distance)}</dd>
              </div>
            </dl>
          </section>
        )}

        {/* Loading State */}
        {isCalculating && (
          <div className="loading-state" role="status" aria-live="polite" aria-busy="true">
            <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
            <span>Calculating {currentMode} route...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-state" role="alert" aria-live="assertive">
            <p>{error}</p>
            <button onClick={() => dispatch(setRouteError(null))} aria-label="Dismiss error message">
              Dismiss
            </button>
          </div>
        )}

        {/* Directions List */}
        {routeSteps.length > 0 && !isCalculating && !error && (
          <section className="directions-section" aria-labelledby="directions-heading">
            <h3 id="directions-heading">Turn-by-turn Directions</h3>
            <ol className="directions-list">
              {routeSteps.map((step, index) => (
                <li key={index} className="direction-step">
                  <div className="step-icon" aria-hidden="true">
                    <FontAwesomeIcon
                      icon={getManeuverIcon(step.maneuver?.type || 'straight', step.maneuver?.modifier)}
                    />
                  </div>
                  <div className="step-content">
                    <div className="step-instruction">
                      {step.maneuver.instruction}
                    </div>
                    <div className="step-details">
                      {step.name && (
                        <span className="step-name">on {step.name}</span>
                      )}
                      <span className="step-distance">
                        {formatRouteDistance(step.distance)}
                      </span>
                      {step.duration > 0 && (
                        <span className="step-duration">
                          ({formatRouteDuration(step.duration)})
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </aside>
  );
}
